import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight,
  Clock, Users, Edit, RefreshCw, BookOpen, Building2,
  Monitor, FileText, CheckCircle, GraduationCap, Save, Download, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';

// Event type configuration
const EVENT_TYPES = {
  cours: { label: "Cours", color: "bg-violet-500", icon: BookOpen },
  entreprise: { label: "Entreprise", color: "bg-amber-500", icon: Building2 },
  cours_en_ligne: { label: "Cours en ligne", color: "bg-blue-500", icon: Monitor },
  cours_physique: { label: "Cours physique", color: "bg-emerald-500", icon: GraduationCap },
  examen: { label: "Examen", color: "bg-red-500", icon: FileText },
  revision: { label: "Révision", color: "bg-orange-500", icon: RefreshCw },
  devoir: { label: "Devoir", color: "bg-pink-500", icon: CheckCircle }
};

/** Local YYYY-MM-DD — never use toISOString() for calendar days (UTC shift). */
const toLocalDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const TrainerPlanningPage = ({ onMenuClick }) => {
  // Data states
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Calendar states
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Modal states
  const [showEventModal, setShowEventModal] = useState(false);
  const [showEnrichModal, setShowEnrichModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Create event modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [groups, setGroups] = useState([]);
  const [newEvent, setNewEvent] = useState({
    title: '', description: '', event_type: 'cours',
    event_date: '', start_time: '09:00', end_time: '10:00',
    group_id: '', location: ''
  });

  // Enrichment form
  const [enrichment, setEnrichment] = useState({
    trainer_comment: '',
    trainer_instructions: '',
    trainer_resources: ''
  });

  // Fetch trainer events
  const fetchEvents = useCallback(async () => {
    try {
      const response = await api.get('/planning/trainer/events');
      setEvents(response.data);
    } catch (error) {
      console.error('Erreur chargement événements:', error);
      // Fallback to empty if API not found
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
    api.get('/groups').then(r => setGroups(r.data || [])).catch(() => {});
  }, [fetchEvents]);

  const handleCreateEvent = async () => {
    if (!newEvent.title || !newEvent.event_date) {
      toast.error('Titre et date requis');
      return;
    }
    setSaving(true);
    try {
      await api.post('/planning/trainer/events', {
        ...newEvent,
        group_id: newEvent.group_id ? parseInt(newEvent.group_id) : null,
      });
      toast.success('Événement ajouté');
      setShowCreateModal(false);
      setNewEvent({ title: '', description: '', event_type: 'cours', event_date: '', start_time: '09:00', end_time: '10:00', group_id: '', location: '' });
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  // Calendar navigation
  const goToPrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    
    startDate.setDate(startDate.getDate() - ((startDate.getDay() + 6) % 7));
    
    const days = [];
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (7 - endDate.getDay()) % 7);
    
    const current = new Date(startDate);
    while (current <= endDate) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };

  // Get events for a specific day
  const getEventsForDay = (day) => {
    const dayStr = toLocalDateKey(day);
    return events.filter(e => e.event_date === dayStr);
  };

  // Open event detail modal
  const openEventModal = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  // Open enrichment modal
  const openEnrichModal = () => {
    setEnrichment({
      trainer_comment: selectedEvent?.trainer_comment || '',
      trainer_instructions: selectedEvent?.trainer_instructions || '',
      trainer_resources: selectedEvent?.trainer_resources || ''
    });
    setShowEventModal(false);
    setShowEnrichModal(true);
  };

  // Handle save enrichment
  const handleSaveEnrichment = async () => {
    if (!selectedEvent) return;
    
    setSaving(true);
    try {
      await api.put(`/planning/trainer/events/${selectedEvent.id}/enrich`, enrichment);
      toast.success('Notes mises à jour');
      setShowEnrichModal(false);
      fetchEvents();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Format time
  const formatTime = (time) => time?.substring(0, 5) || '';

  // Get month/year title
  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  const calendarDays = generateCalendarDays();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Count upcoming events
  const upcomingEvents = events.filter(e => new Date(e.event_date) >= today).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <Header onMenuClick={onMenuClick} />
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
              <CalendarIcon className="w-8 h-8 text-violet-600" />
              Mon Planning
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">
              {upcomingEvents} événement(s) à venir
            </p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setShowCreateModal(true)}>
              <Plus className="w-4 h-4 mr-1" /> Ajouter un événement
            </Button>
            <Button variant="outline" size="sm" onClick={async () => {
              try {
                const res = await api.get(`/planning/trainer/export/pdf?view=month&month=${currentDate.getMonth() + 1}&year=${currentDate.getFullYear()}`, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a'); a.href = url; a.download = `planning_mois_${currentDate.getMonth() + 1}_${currentDate.getFullYear()}.pdf`; a.click();
              } catch { toast.error('Erreur export PDF'); }
            }}>
              <Download className="w-4 h-4 mr-1" /> PDF Mois
            </Button>
            <Button variant="outline" size="sm" onClick={async () => {
              try {
                const res = await api.get(`/planning/trainer/export/pdf?view=year&year=${currentDate.getFullYear()}`, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a'); a.href = url; a.download = `planning_annuel_${currentDate.getFullYear()}.pdf`; a.click();
              } catch { toast.error('Erreur export PDF'); }
            }}>
              <Download className="w-4 h-4 mr-1" /> PDF Année
            </Button>
          </div>
        </div>

        {/* Navigation */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={goToPrevMonth}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={goToToday}>
                  Aujourd'hui
                </Button>
                <Button variant="outline" size="sm" onClick={goToNextMonth}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              
              <div className="w-32"></div>
            </div>
          </CardContent>
        </Card>

        {/* Calendar */}
        <Card>
          <CardContent className="p-4">
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map(day => (
                <div key={day} className="text-center text-sm font-medium text-slate-500 py-2">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, idx) => {
                const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                const isToday = day.getTime() === today.getTime();
                const dayEvents = getEventsForDay(day);
                
                return (
                  <div
                    key={idx}
                    className={`min-h-24 p-1 border rounded-lg ${
                      isCurrentMonth 
                        ? 'bg-white dark:bg-slate-800' 
                        : 'bg-slate-50 dark:bg-slate-900/50'
                    } ${isToday ? 'ring-2 ring-violet-500' : 'border-slate-200 dark:border-slate-700'}`}
                  >
                    <div className={`text-sm font-medium mb-1 ${
                      isToday 
                        ? 'text-violet-600' 
                        : isCurrentMonth 
                          ? 'text-slate-900 dark:text-white' 
                          : 'text-slate-400'
                    }`}>
                      {day.getDate()}
                    </div>
                    
                    {/* Events */}
                    <div className="space-y-1">
                      {dayEvents.slice(0, 3).map(event => {
                        const typeConfig = EVENT_TYPES[event.event_type] || EVENT_TYPES.cours;
                        return (
                          <div
                            key={event.id}
                            onClick={() => openEventModal(event)}
                            className={`text-xs px-1.5 py-0.5 rounded truncate text-white ${typeConfig.color} cursor-pointer hover:opacity-80`}
                          >
                            {formatTime(event.start_time)} {event.title}
                          </div>
                        );
                      })}
                      {dayEvents.length > 3 && (
                        <div className="text-xs text-slate-500 pl-1">
                          +{dayEvents.length - 3} autres
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Legend */}
        <Card className="mt-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Légende :</span>
              {Object.entries(EVENT_TYPES).map(([key, config]) => (
                <div key={key} className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded ${config.color}`}></div>
                  <span className="text-sm text-slate-600 dark:text-slate-400">{config.label}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Events List */}
        {events.filter(e => new Date(e.event_date) >= today).length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-lg">Prochains événements</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {events
                  .filter(e => new Date(e.event_date) >= today)
                  .sort((a, b) => new Date(a.event_date) - new Date(b.event_date))
                  .slice(0, 5)
                  .map(event => {
                    const typeConfig = EVENT_TYPES[event.event_type] || EVENT_TYPES.cours;
                    return (
                      <div
                        key={event.id}
                        onClick={() => openEventModal(event)}
                        className="flex items-center gap-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700"
                      >
                        <div className={`w-2 h-12 rounded ${typeConfig.color}`}></div>
                        <div className="flex-1">
                          <h4 className="font-medium text-slate-900 dark:text-white">{event.title}</h4>
                          <p className="text-sm text-slate-500">
                            {new Date(event.event_date).toLocaleDateString('fr-FR')} • {formatTime(event.start_time)} - {formatTime(event.end_time)}
                          </p>
                          {event.group_name && (
                            <p className="text-sm text-slate-500 flex items-center gap-1">
                              <Users className="w-3 h-3" /> {event.group_name}
                            </p>
                          )}
                        </div>
                        <Button variant="ghost" size="sm">
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Event Detail Modal */}
      <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedEvent && EVENT_TYPES[selectedEvent.event_type] && (
                <div className={`w-3 h-3 rounded ${EVENT_TYPES[selectedEvent.event_type].color}`}></div>
              )}
              {selectedEvent?.title}
            </DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                <CalendarIcon className="w-4 h-4" />
                {new Date(selectedEvent.event_date).toLocaleDateString('fr-FR', { 
                  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                })}
              </div>
              
              <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                <Clock className="w-4 h-4" />
                {formatTime(selectedEvent.start_time)} - {formatTime(selectedEvent.end_time)}
              </div>
              
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded text-sm text-white ${EVENT_TYPES[selectedEvent.event_type]?.color || 'bg-slate-500'}`}>
                  {selectedEvent.event_type_label}
                </span>
              </div>
              
              {selectedEvent.group_name && (
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                  <Users className="w-4 h-4" />
                  Groupe : {selectedEvent.group_name}
                </div>
              )}
              
              {selectedEvent.course_name && (
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                  <BookOpen className="w-4 h-4" />
                  Cours : {selectedEvent.course_name}
                </div>
              )}
              
              {selectedEvent.location && (
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                  <Building2 className="w-4 h-4" />
                  Lieu : {selectedEvent.location}
                </div>
              )}
              
              {selectedEvent.description && (
                <div className="pt-2 border-t">
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {selectedEvent.description}
                  </p>
                </div>
              )}
              
              {/* Trainer enrichment display */}
              {(selectedEvent.trainer_comment || selectedEvent.trainer_instructions || selectedEvent.trainer_resources) && (
                <div className="pt-2 border-t bg-violet-50 dark:bg-violet-900/20 p-3 rounded-lg">
                  <h4 className="font-medium text-violet-700 dark:text-violet-300 mb-2">
                    Vos notes
                  </h4>
                  {selectedEvent.trainer_comment && (
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                      {selectedEvent.trainer_comment}
                    </p>
                  )}
                  {selectedEvent.trainer_instructions && (
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                      <strong>Instructions :</strong> {selectedEvent.trainer_instructions}
                    </p>
                  )}
                  {selectedEvent.trainer_resources && (
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      <strong>Ressources :</strong> {selectedEvent.trainer_resources}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEventModal(false)}>
              Fermer
            </Button>
            <Button onClick={openEnrichModal} className="bg-violet-600 hover:bg-violet-700">
              <Edit className="w-4 h-4 mr-2" />
              Ajouter des notes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Event Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-violet-600" />
              Ajouter un événement
            </DialogTitle>
            <DialogDescription>Créez un événement sur votre planning</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Titre *</Label>
              <Input value={newEvent.title} onChange={e => setNewEvent({...newEvent, title: e.target.value})} placeholder="Ex: Cours de management" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={newEvent.event_date} onChange={e => setNewEvent({...newEvent, event_date: e.target.value})} />
              </div>
              <div>
                <Label>Type</Label>
                <select className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" value={newEvent.event_type} onChange={e => setNewEvent({...newEvent, event_type: e.target.value})}>
                  {Object.entries(EVENT_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Début</Label>
                <Input type="time" value={newEvent.start_time} onChange={e => setNewEvent({...newEvent, start_time: e.target.value})} />
              </div>
              <div>
                <Label>Fin</Label>
                <Input type="time" value={newEvent.end_time} onChange={e => setNewEvent({...newEvent, end_time: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Groupe</Label>
              <select className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" value={newEvent.group_id} onChange={e => setNewEvent({...newEvent, group_id: e.target.value})}>
                <option value="">Aucun groupe</option>
                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Lieu</Label>
              <Input value={newEvent.location} onChange={e => setNewEvent({...newEvent, location: e.target.value})} placeholder="Salle, lien visio..." />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={newEvent.description} onChange={e => setNewEvent({...newEvent, description: e.target.value})} rows={2} placeholder="Détails..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Annuler</Button>
            <Button onClick={handleCreateEvent} disabled={saving} className="bg-violet-600 hover:bg-violet-700">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enrichment Modal */}
      <Dialog open={showEnrichModal} onOpenChange={setShowEnrichModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-violet-600" />
              Enrichir l'événement
            </DialogTitle>
            <DialogDescription>
              Ajoutez des commentaires, instructions ou ressources pour vos étudiants
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Commentaire pédagogique</Label>
              <Textarea
                value={enrichment.trainer_comment}
                onChange={(e) => setEnrichment({ ...enrichment, trainer_comment: e.target.value })}
                placeholder="Ajouter un commentaire sur cette séance..."
                rows={3}
              />
            </div>
            
            <div>
              <Label>Instructions pour les étudiants</Label>
              <Textarea
                value={enrichment.trainer_instructions}
                onChange={(e) => setEnrichment({ ...enrichment, trainer_instructions: e.target.value })}
                placeholder="Instructions spécifiques, prérequis, matériel à apporter..."
                rows={3}
              />
            </div>
            
            <div>
              <Label>Ressources / Liens</Label>
              <Input
                value={enrichment.trainer_resources}
                onChange={(e) => setEnrichment({ ...enrichment, trainer_resources: e.target.value })}
                placeholder="Liens vers des ressources, documents, vidéos..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEnrichModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveEnrichment} disabled={saving} className="bg-violet-600 hover:bg-violet-700">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrainerPlanningPage;
